<template>
  <div :id="`${source}_meeting_talktime`" class="pt-4 proxima_nova">
    <div v-if="participantsList?.length > 0" class="flex flex-col gap-2">
      <CustomListSelector
        :source="source"
        :list="participantsListForSelector"
        v-model="selectedParticipantsIndexes"
        :orientation="customSelectorDirections.HORIZONTAL"
      />

      <div class="flex flex-col w-full px-3">
        <TalktimeTracker
          v-if="hasSelectedParticipants"
          :source="source"
          :max-height="dynamicMaxHeight"
        />

        <EmptyScreen
          v-else
          padding="mb-4 mt-2"
          css="size-16 text-sm"
          source="meeting_talktime"
          text="Please select a participant"
        />
      </div>
    </div>

    <EmptyScreen
      v-else
      source="meeting_talktime"
      text="No talktime found yet!"
    />
  </div>
</template>

<script setup>
import { computed, watch, onMounted, ref, inject } from "vue";
import { customSelectorDirections } from "@/src/constants/customComponentConstants.js";
import store from "@/src/store/index.js";

const storeUsers = computed(() => store.getters["users/users"] || []);
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import TalktimeTracker from "@/src/components/meetings_components/TalktimeTracker.vue";
import CustomListSelector from "@/src/components/custom_components/CustomListSelector.vue";

const props = defineProps({
  source: { type: String, required: true },
  data: { type: Object, required: true },
});

const videoPlayerHeight = inject("videoPlayerHeight", ref(0));

const participantsList = computed(() => {
  if (!props.data?.participants) return [];
  return Object.entries(props.data.participants);
});

const participantsListForSelector = computed(() => {
  if (!props.data?.participants) return {};

  const selectorList = {};
  Object.entries(props.data.participants).forEach(([id, name], index) => {
    const lowerName = name.toLowerCase();
    const matchedUser = storeUsers.value.find(
      (u) => u.name?.toLowerCase() === lowerName || u.email?.toLowerCase() === lowerName,
    );
    selectorList[index] = { name, avatar_color: matchedUser?.avatar_color || null };
  });
  return selectorList;
});

const selectedParticipantsIds = computed(
  () => store.getters["meetings/recordingSelectedParticipants"] || [],
);

const selectedParticipantsIndexes = computed({
  get: () => {
    return participantsList.value
      .map(([participantId], index) =>
        selectedParticipantsIds.value.includes(participantId) ? index : null,
      )
      .filter((index) => index !== null);
  },
  set: (indexes) => {
    const participantIds = indexes
      .map((index) => {
        const participantEntry = participantsList.value[index];
        return participantEntry ? participantEntry[0] : null;
      })
      .filter(Boolean);

    store.dispatch("meetings/setRecordingSelectedParticipants", participantIds);
  },
});

const hasSelectedParticipants = computed(
  () => selectedParticipantsIds.value.length > 0,
);

const dynamicMaxHeight = computed(() => {
  const getBannerHeight = () => {
    const bannerHeight = getComputedStyle(document.documentElement)
      .getPropertyValue("--banner-height")
      .replace("px", "");
    return parseInt(bannerHeight) || 0;
  };

  const baseOffset = 282;
  const bannerHeight = getBannerHeight();
  const videoHeight = videoPlayerHeight.value || 0;

  return `calc(100vh - ${baseOffset - bannerHeight + videoHeight}px)`;
});

const initializeSelectedParticipants = () => {
  if (
    participantsList.value.length > 0 &&
    selectedParticipantsIds.value.length === 0
  ) {
    const allParticipantIds = participantsList.value.map(([id]) => id);
    store.dispatch(
      "meetings/setRecordingSelectedParticipants",
      allParticipantIds,
    );
  }
};

onMounted(() => {
  initializeSelectedParticipants();
});

watch(
  () => props.data,
  (newData) => {
    if (newData?.transcript && newData?.participants) {
      store.dispatch("meetings/setRecordingTranscriptData", {
        transcript: newData.transcript,
        participants: newData.participants,
      });

      initializeSelectedParticipants();
    }
  },
  { immediate: true },
);
</script>
